import { ApiError, apiRequest } from './http';
import type { DocumentPage, DocumentSummary } from './types';

const documentsPath = '/api/documents';

/** Paged via continuation token (LB-10) — the legacy library loaded everything. */
export async function listDocuments(options: { pageSize?: number; continuationToken?: string } = {}): Promise<DocumentPage> {
  const query = new URLSearchParams();
  if (options.pageSize) query.set('pageSize', String(options.pageSize));
  if (options.continuationToken) query.set('continuationToken', options.continuationToken);

  const suffix = query.toString();
  const response = await apiRequest<{ items?: DocumentSummary[]; continuationToken?: string | null }>(
    suffix ? `${documentsPath}?${suffix}` : documentsPath
  );

  return { items: response.items ?? [], continuationToken: response.continuationToken ?? null };
}

export function documentDownloadUrl(documentId: string): string {
  return `${documentsPath}/${encodeURIComponent(documentId)}/content`;
}

/** The bytes themselves, for handing to another service (e.g. a SharePoint upload). */
export async function downloadDocumentBlob(documentId: string): Promise<Blob> {
  const path = documentDownloadUrl(documentId);
  const response = await fetch(path, { credentials: 'include' });
  if (!response.ok) throw new ApiError(`Request failed with ${response.status}`, response.status, path, null);
  return response.blob();
}

export async function getDocument(documentId: string): Promise<DocumentSummary> {
  return apiRequest<DocumentSummary>(`${documentsPath}/${encodeURIComponent(documentId)}`);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export async function deleteDocument(documentId: string): Promise<void> {
  await apiRequest<void>(`${documentsPath}/${encodeURIComponent(documentId)}`, { method: 'DELETE' });
}
